export type RGBAColor = {
  r: number;
  g: number;
  b: number;
  a: number;
};

export function hexToRGBA(hex: string): RGBAColor {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
        a: 1,
      }
    : {
        r: 0,
        g: 0,
        b: 0,
        a: 1,
      };
}

export const blendRGBAColors = (base: RGBAColor, added: RGBAColor): string => {
  const mix = { r: 0, g: 0, b: 0, a: 0 };
  mix.a = 1 - (1 - added.a) * (1 - base.a);
  mix.r = Math.round(
    (added.r * added.a) / mix.a + (base.r * base.a * (1 - added.a)) / mix.a,
  );
  mix.g = Math.round(
    (added.g * added.a) / mix.a + (base.g * base.a * (1 - added.a)) / mix.a,
  );
  mix.b = Math.round(
    (added.b * added.a) / mix.a + (base.b * base.a * (1 - added.a)) / mix.a,
  );
  return `rgba(${mix.r}, ${mix.g}, ${mix.b}, ${mix.a})`;
};

export const getBrandBillboardColor = (brandHexColor: string): string => {
  const brandColor = hexToRGBA(brandHexColor);
  const gray1 = hexToRGBA("#f7f7f7");
  return blendRGBAColors(gray1, {
    ...brandColor,
    a: 0.05,
  });
};

export function shadeColor(hex: string, opacity = 1): string {
  const multiplier = 1 - opacity;
  const R_old = parseInt(hex.substring(1, 3), 16);
  const G_old = parseInt(hex.substring(3, 5), 16);
  const B_old = parseInt(hex.substring(5, 7), 16);
  const R_new = Math.min(Math.round(R_old + (255 - R_old) * multiplier), 255);
  const G_new = Math.min(Math.round(G_old + (255 - G_old) * multiplier), 255);
  const B_new = Math.min(Math.round(B_old + (255 - B_old) * multiplier), 255);
  const RR =
    R_new.toString(16).length === 1
      ? "0" + R_new.toString(16)
      : R_new.toString(16);
  const GG =
    G_new.toString(16).length === 1
      ? "0" + G_new.toString(16)
      : G_new.toString(16);
  const BB =
    B_new.toString(16).length === 1
      ? "0" + B_new.toString(16)
      : B_new.toString(16);
  return "#" + RR + GG + BB;
}

export function darkenColor(hex: string, factor = 1): string {
  factor = Math.max(0, Math.min(1, factor));

  const R = parseInt(hex.substring(1, 3), 16);
  const G = parseInt(hex.substring(3, 5), 16);
  const B = parseInt(hex.substring(5, 7), 16);

  const R_new = Math.round(R * (1 - factor));
  const G_new = Math.round(G * (1 - factor));
  const B_new = Math.round(B * (1 - factor));

  const RR = R_new.toString(16).padStart(2, "0");
  const GG = G_new.toString(16).padStart(2, "0");
  const BB = B_new.toString(16).padStart(2, "0");

  return "#" + RR + GG + BB;
}

export function addOpacityToHexColor(hex: string, alpha = 1): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const isValidHexColor = (color: string): boolean => {
  return /^#[0-9A-F]{6}$/i.test(color);
};

export const gradients: { [key: string]: [string, string] } = {
  yellowViolet: ["#fff9c5", "#8e8bff"],
  indigoAqua: ["#dffff9", "#bfbaff"],
  redIndigo: ["#ffb9b9", "#c99fff"],
  orangeYellow: ["#ffc8c8", "#fff4cd"],
  cyanIndigo: ["#bbfbff", "#849fff"],
  greenBlue: ["#c9ffd5", "#6d9de6"],
  peachPurple: ["#a8acfb", "#ffdfef"],
  magentaPeach: ["#686fab", "#efccff"],
  yellowOrange: ["#f8ffe2", "#ffD4bc"],
  yellowPink: ["#fef1c2", "#feb0fb"],
  redPink: ["#ff9494", "#ffd2fd"],
  mintPink: ["#c6ffdc", "#f4b0ff"],
  pinkYellow: ["#e8bafb", "#eff9df"],
};

export const gradientsArray = Object.values(gradients);
